import React from 'react';
import PropTypes from 'prop-types';

import {ButtonBase, Popper, TextField, Typography} from 'views/components';
import {translate} from 'views/module';

const ActionLogTextbox = (props) => {
  const {
    onSubmit,
  } = props;
  const [message, setMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hudsonMentionRange, setHudsonMentionRange] = React.useState(null);
  const inputRef = React.useRef(null);
  const hudsonMention = '@Hudson';

  const getHudsonMentionRange = (value, cursorIndex) => {
    const beforeCursor = value.slice(0, cursorIndex);
    const match = beforeCursor.match(/(^|\s)@([^\s]*)$/);

    if (!match || !'hudson'.startsWith(match[2].toLowerCase()) || match[2].toLowerCase() === 'hudson') {
      return null;
    }

    return {
      start: beforeCursor.length - match[0].length + match[1].length,
      end: cursorIndex,
    };
  };

  const getCurrentMessage = () => {
    const currentValue = inputRef.current?.value;

    return typeof currentValue === 'string' ? currentValue : message;
  };
  const getCursorIndex = () => inputRef.current?.selectionStart ?? getCurrentMessage().length;

  const updateHudsonMentionRange = (nextMessage) => {
    setHudsonMentionRange(getHudsonMentionRange(nextMessage, getCursorIndex()));
  };

  const handleMessageChange = (nextMessage) => {
    setMessage(nextMessage);
    updateHudsonMentionRange(nextMessage);
  };

  const handleCursorUpdate = () => {
    updateHudsonMentionRange(getCurrentMessage());
  };

  const selectHudsonMention = () => {
    const currentMessage = getCurrentMessage();
    const range = hudsonMentionRange || getHudsonMentionRange(currentMessage, getCursorIndex());

    if (!range) {
      return;
    }

    const nextMessage = `${currentMessage.slice(0, range.start)}${hudsonMention} ${currentMessage.slice(range.end)}`;
    const nextCursorIndex = range.start + hudsonMention.length + 1;

    setMessage(nextMessage);
    setHudsonMentionRange(null);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCursorIndex, nextCursorIndex);
    });
  };

  const submitMessage = async () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const saved = await onSubmit(trimmedMessage);

      if (saved) {
        setMessage('');
        setHudsonMentionRange(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (event) => {
    if (hudsonMentionRange && ['Enter', 'Tab'].includes(event.key)) {
      event.preventDefault();
      selectHudsonMention();
      return;
    }

    if (hudsonMentionRange && event.key === 'Escape') {
      event.preventDefault();
      setHudsonMentionRange(null);
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    submitMessage();
  };

  return (
    <>
      <Popper
        open={Boolean(hudsonMentionRange) && !isSubmitting}
        anchorEl={inputRef.current}
        placement='top-start'
        onClickAway={() => setHudsonMentionRange(null)}
        zIndex={1400}
      >
        <ButtonBase
          onMouseDown={(event) => event.preventDefault()}
          onClick={selectHudsonMention}
          sx={{
            'justifyContent': 'flex-start',
            'minWidth': '160px',
            'px': '14px',
            'py': '10px',
            '&:hover': {
              backgroundColor: '#F8F6F3',
            },
          }}
        >
          <Typography sx={{fontSize: '12px', color: '#1A1916'}}>
            @Hudson
          </Typography>
        </ButtonBase>
      </Popper>
      <TextField
        inputRef={inputRef}
        value={message}
        onChange={handleMessageChange}
        onClick={handleCursorUpdate}
        onKeyDown={handleKeyDown}
        onKeyUp={handleCursorUpdate}
        fullWidth
        placeholder={translate('case.Detail.ActionLog.Placeholder')}
        autoComplete='off'
        disabled={isSubmitting}
        sx={{
          'width': '100%',
          '& .MuiOutlinedInput-root': {
            'backgroundColor': 'common.white',
            'borderRadius': '9px',
            'height': '42px',
            '& fieldset': {
              'borderStyle': 'solid',
              'borderColor': '#DCDDE5',
            },
            '&:hover fieldset': {
              'borderColor': '#D1D3DD',
            },
          },
          '& .MuiOutlinedInput-input': {
            'px': '16px',
            'py': '10px',
            'fontSize': '13px',
            'lineHeight': '150%',
            'whiteSpace': 'pre',
            'color': '#050A14',
            'caretColor': '#050A14',
          },
          '& .MuiOutlinedInput-input::placeholder': {
            'color': '#7F8493',
            'opacity': 1,
          },
        }}
      />
    </>
  );
};

ActionLogTextbox.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default React.memo(ActionLogTextbox);
